import { Fragment, useEffect, useState, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { parentScrollToTheTop } from "../..//lib/utils";
import { XMarkIcon } from "@heroicons/react/24/outline";
import styles from "../../styles/Form.module.css";
import classNames from "classnames";
import useAutosize from "../../lib/use_autosize";

export default function HDialog({
  children,
  fullHeight,
  maxWidthClass = "max-w-2xl",
  noAutosizing,
  onHide,
  pagePath,
  show = false,
  widthClass = "w-full",
}: {
  children: JSX.Element;
  fullHeight?: boolean;
  maxWidthClass?: string;
  noAutosizing?: boolean;
  pagePath?: string; // For analytics
  widthClass?: string;
  onHide: () => void;
  show: boolean;
}) {
  const [parentScroll, setParentScroll] = useState(0);
  const dialogRef = useRef();
  const [setElement, element] = useAutosize({
    enabled: show && !noAutosizing,
    label: "simpledialog",
  });

  useEffect(() => {
    if (parentScroll == 0) {
      setParentScroll(1);
      return;
    } else parentScrollToTheTop();
  }, [show]);

  return (
    <Transition show={show} as={Fragment}>
      <Dialog
        as="div"
        key={show.toString()}
        className="fixed z-50 inset-0 overflow-y-auto sm:overflow-hidden"
        onClose={onHide}
      >
        <div
          className="flex text-center md:block md:px-2 lg:px-4"
          style={{ fontSize: 0 }}
          ref={(el) => {
            if (!dialogRef.current && !element) setElement(el);
            dialogRef.current = el;
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500/75 transition-opacity block" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="hidden md:inline-block" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
            enterTo="opacity-100 translate-y-0 md:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 md:scale-100"
            leaveTo="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
          >
            <div
              className={`relative  overflow-auto flex self-center text-base text-left transition ${widthClass} md:inline-block md:${maxWidthClass} md:px-4 ${
                fullHeight ? "md:my-2" : "md:my-8"
              } md:align-middle`}
            >
              <div
                className={`autosize w-full ${
                  fullHeight
                    ? "md:overflow-auto md:h-[calc(100vh-1em)] h-[calc(100vh)] "
                    : "overflow-hidden"
                } md:rounded-lg relative flex items-top bg-white px-4 pt-14 pb-8 overflow-hidden shadow-2xl sm:px-6 sm:pt-8 md:p-6 lg:p-8`}
              >
                <button
                  aria-label="Close"
                  type="button"
                  className={classNames(
                    styles.link,
                    "absolute top-4 right-4 text-gray-400 hover:text-gray-500 sm:top-8 sm:right-6 md:top-6 md:right-6 lg:top-8 lg:right-8 rounded-md z-50",
                  )}
                  onClick={onHide}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>

                <div className="w-full">{children}</div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
